import { FormEvent, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ApiError, api } from "../api/client";
import { AuthSession, AuthUser, useAuth } from "../auth/AuthContext";
import "./LoginPage.css";

type LoginResponse = {
  access_token: string;
  user: AuthUser;
};

export function LoginPage() {
  const { isAuthenticated, session, setSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [courseName, setCourseName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const sessionExpired = new URLSearchParams(location.search).get("expired") === "1";
  const [error, setError] = useState<string | null>(
    sessionExpired ? "Oturum Süreniz Doldu. Lütfen Tekrar Giriş Yapın." : null,
  );
  const [submitting, setSubmitting] = useState(false);

  if (isAuthenticated) {
    const role = session?.user?.role;
    if (role === "student") {
      return <Navigate to="/m/student" replace />;
    }
    if (role === "instructor") {
      const isMobileViewport =
        typeof window !== "undefined" &&
        (window.innerWidth < 768 || window.matchMedia("(pointer: coarse)").matches);
      return <Navigate to={isMobileViewport ? "/m/instructor" : "/dashboard"} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const response = await api.post<LoginResponse>(
        "/auth/login",
        { tenantName: courseName.trim(), email: email.trim(), password },
        { authenticated: false },
      );
      const session: AuthSession = { token: response.access_token, user: response.user };
      setSession(session);
      const fromState = (location.state as { from?: string } | null)?.from;
      const isInstructor = response.user.role === "instructor";
      const isStudent = response.user.role === "student";
      const isMobileViewport =
        typeof window !== "undefined" &&
        (window.innerWidth < 768 || window.matchMedia("(pointer: coarse)").matches);
      let redirectTo = fromState || "/dashboard";
      if (!fromState && isInstructor && isMobileViewport) {
        redirectTo = "/m/instructor";
      } else if (!fromState && isStudent) {
        // Kursiyerler her zaman mobil panele yonlendirilir (PC'de de mobil shell aciliyor)
        redirectTo = "/m/student";
      }
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "RATE_LIMITED") {
          setError("Çok Fazla Deneme Yaptınız. Lütfen Biraz Sonra Tekrar Deneyin.");
        } else if (err.status === 401) {
          setError("Kullanıcı Adı Veya Şifre Hatalı.");
        } else {
          setError(err.message || "Giriş Yapılamadı.");
        }
      } else {
        setError("Sunucuya Ulaşılamadı. Bağlantınızı Kontrol Edin.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="kp-login">
      <form className="kp-login__card kp-card" onSubmit={handleSubmit}>
        <div className="kp-login__header">
          <div className="kp-login__brand">KP</div>
          <h1>KURS PANELİM</h1>
          <p>Sürücü Kursu Yönetim Paneline Giriş Yapın.</p>
        </div>

        <label className="kp-login__field">
          <span>Kurs Adı</span>
          <input
            className="kp-input"
            type="text"
            value={courseName}
            onChange={(e) => setCourseName(e.target.value)}
            required
            autoComplete="off"
          />
          <small className="kp-login__hint">Size iletilen sürücü kursu adını yazın.</small>
        </label>

        <label className="kp-login__field">
          <span>E-posta</span>
          <input
            className="kp-input"
            type="email"
            placeholder="kullanici@kurum.com"
            value={email}
            onChange={(e) => setEmail(e.target.value.trim())}
            required
            autoComplete="username"
          />
        </label>

        <label className="kp-login__field">
          <span>Şifre</span>
          <input
            className="kp-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
        </label>

        {error && <div className="kp-login__error">{error}</div>}

        <button className="kp-btn kp-btn--primary kp-login__submit" type="submit" disabled={submitting}>
          {submitting ? "Giriş Yapılıyor…" : "Giriş Yap"}
        </button>
      </form>
    </div>
  );
}
